import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import AuthLayout from '../components/auth/AuthLayout';
import EmailStep from '../components/auth/EmailStep';
import MfaStep from '../components/auth/MfaStep';
import PasswordStep from '../components/auth/PasswordStep';
import RequestAccessStep from '../components/auth/RequestAccessStep';
import SsoStep from '../components/auth/SsoStep';
import { createBrowserChallenge } from '../lib/ssoCode';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AUTH_FAILURE = 'We could not sign you in. Check your details or contact your organisation administrator.';
const NETWORK_FAILURE = 'Qflow services are temporarily unavailable. Try again shortly.';
const MFA_FAILURE = 'That code is not valid. Check your authenticator app and try again.';

const safeReturnTo = (value) => {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/auth')) return '/';
  return value;
};

const SSO_ERRORS = {
  not_invited: 'Your organisation account is not set up in Qflow yet. Ask your Qflow administrator to invite you.',
  wrong_domain: 'That account is not on your organisation’s email domain. Sign in with your work account.',
  expired: 'Your sign-in took too long. Start again.',
  failed: 'Single sign-on did not complete. Try again, or contact your organisation administrator.',
  unavailable: 'Single sign-on is unavailable. Try password sign-in or contact your organisation administrator.',
};

const safeAuthPath = (value) => {
  if (!value || !value.startsWith('/api/auth/') || value.startsWith('//')) return '';
  return value;
};

const configuredProvider = (id, label, enabled, startPath) => (
  enabled === 'true' && safeAuthPath(startPath) ? { id, label, startPath } : null
);

const Auth = ({ setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organisation, setOrganisation] = useState(null);
  const [sso, setSso] = useState(null);
  const [loginOrganisations, setLoginOrganisations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => SSO_ERRORS[searchParams.get('sso_error')] || '');
  const [mfa, setMfa] = useState(null);

  const providers = useMemo(() => [
    configuredProvider('microsoft', 'Microsoft', process.env.REACT_APP_MICROSOFT_AUTH_ENABLED, process.env.REACT_APP_MICROSOFT_AUTH_START_PATH),
    configuredProvider('google', 'Google', process.env.REACT_APP_GOOGLE_AUTH_ENABLED, process.env.REACT_APP_GOOGLE_AUTH_START_PATH),
  ].filter(Boolean), []);

  const startExternalAuthentication = async (startPath) => {
    const path = safeAuthPath(startPath);
    if (!path) {
      setError('Single sign-on is unavailable. Try password sign-in or contact your organisation administrator.');
      return;
    }
    setBusy(true);
    // The API runs on its own address; SSO starts there, not on this site.
    const destination = new URL(path, process.env.REACT_APP_BACKEND_URL || window.location.origin);
    destination.searchParams.set('returnTo', returnTo);
    try {
      destination.searchParams.set('client_challenge', await createBrowserChallenge());
      window.location.assign(destination.toString());
    } catch { setBusy(false); setError('Could not start secure sign-in in this browser. Try again.'); }
  };

  const continueFromEmail = async (event) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalized)) {
      setError('Enter a valid work email address.');
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    setEmail(normalized);
    try {
      const { data } = await axiosInstance.post('/auth/discover', { email: normalized });
      setOrganisation(data.organisation || null);
      if (data.route === 'sso' && safeAuthPath(data.sso?.start_path)) {
        setSso(data.sso);
        setStep('sso');
      } else {
        setStep('password');
      }
    } catch (requestError) {
      setOrganisation(null);
      setStep('password');
    } finally {
      setBusy(false);
    }
  };

  const finishAuthentication = (data) => {
    localStorage.setItem('token', data.token);
    setUser(data.user);
    navigate(returnTo, { replace: true });
  };

  const submitLogin = async (event, organizationId = '') => {
    event?.preventDefault();
    if (!password) {
      setError(AUTH_FAILURE);
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.post('/auth/login', {
        email,
        password,
        ...(organizationId ? { organization_id: organizationId } : {}),
      });
      if (data.organization_selection_required) {
        setLoginOrganisations(data.organizations || []);
      } else if (data.mfa_required || data.mfa_enrollment_required) {
        setMfa({ mode: data.mfa_required ? 'verify' : 'enroll', token: data.mfa_token, enrollment: null, recoveryCodes: null, session: null });
        setPassword('');
        setStep('mfa');
        if (data.mfa_enrollment_required) startEnrollment(data.mfa_token);
      } else {
        finishAuthentication(data);
      }
    } catch (requestError) {
      const unavailable = !requestError.response || requestError.response.status >= 500 || requestError.code === 'ECONNABORTED';
      setError(unavailable ? NETWORK_FAILURE : AUTH_FAILURE);
    } finally {
      setBusy(false);
    }
  };

  const mfaError = (requestError) => {
    const status = requestError.response?.status;
    if (!requestError.response || status >= 500 || requestError.code === 'ECONNABORTED') return NETWORK_FAILURE;
    if (status === 429 || status === 503 || requestError.response?.data?.detail === 'Your sign-in has expired. Start again.') return requestError.response.data.detail;
    return MFA_FAILURE;
  };

  const startEnrollment = async (token) => {
    try {
      const { data } = await axiosInstance.post('/auth/mfa/enroll/challenge/start', { mfa_token: token });
      setMfa((current) => current && { ...current, enrollment: data });
    } catch (requestError) {
      setError(mfaError(requestError));
    }
  };

  const verifyMfa = async (payload) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.post('/auth/mfa/verify', { mfa_token: mfa.token, ...payload });
      finishAuthentication(data);
    } catch (requestError) {
      setError(mfaError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrollment = async (code) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axiosInstance.post('/auth/mfa/enroll/challenge/confirm', { mfa_token: mfa.token, code });
      setMfa((current) => ({ ...current, recoveryCodes: data.recovery_codes, session: data }));
    } catch (requestError) {
      setError(mfaError(requestError));
    } finally {
      setBusy(false);
    }
  };

  const resetToEmail = () => {
    setStep('email');
    setPassword('');
    setOrganisation(null);
    setSso(null);
    setLoginOrganisations([]);
    setMfa(null);
    setError('');
  };

  const submitAccessRequest = async (form) => {
    if (busy) return;
    setBusy(true);
    try {
      await axiosInstance.post('/access-requests', form);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="enterprise-auth-card" aria-live="polite">
        {step === 'email' && <EmailStep email={email} error={error} busy={busy} providers={providers} onEmailChange={(value) => { setEmail(value); setError(''); }} onContinue={continueFromEmail} onProvider={(provider) => startExternalAuthentication(provider.startPath)} onRequestAccess={() => { setError(''); setStep('request'); }} />}
        {step === 'password' && <PasswordStep email={email} organisation={organisation} password={password} error={error} busy={busy} organisations={loginOrganisations} onPasswordChange={(value) => { setPassword(value); setError(''); }} onSubmit={submitLogin} onChooseOrganisation={(id) => submitLogin(null, id)} onBack={resetToEmail} />}
        {step === 'mfa' && mfa && <MfaStep mode={mfa.mode} email={email} enrollment={mfa.enrollment} recoveryCodes={mfa.recoveryCodes} error={error} busy={busy} onVerify={verifyMfa} onConfirmEnrollment={confirmEnrollment} onFinish={() => finishAuthentication(mfa.session)} onBack={resetToEmail} />}
        {step === 'sso' && <SsoStep email={email} organisation={organisation} provider={sso?.provider} error={error} busy={busy} onContinue={() => startExternalAuthentication(sso?.start_path)} onUsePassword={() => { setError(''); setStep('password'); }} onBack={resetToEmail} />}
        {step === 'request' && <RequestAccessStep initialEmail={email} busy={busy} onSubmit={submitAccessRequest} onBack={resetToEmail} />}
      </div>
    </AuthLayout>
  );
};

export default Auth;
